import React, { useEffect, useState } from 'react';
import { Joyride, STATUS, Step, EVENTS, ACTIONS } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';
import { appTheme, fontFamily } from '../../styles/theme';
import { getStepsForRole } from '../../data/onboardingSteps';

export const OnboardingWizard: React.FC = () => {
  const { t, direction } = useLanguage();
  const { run, stepIndex, setStepIndex, setRun, finish, dismiss } = useOnboarding();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = direction === 'rtl';

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Get role-specific steps
  const steps = React.useMemo(() => {
    const role = user?.role || 'restaurant_owner';
    return getStepsForRole(role, t, isRTL);
  }, [t, isRTL, user?.role]);

  const handleJoyrideCallback = (data: any) => {
    const { action, index, status, type } = data;

    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      if (status === STATUS.FINISHED) {
        finish();
      } else {
        dismiss();
      }
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      const nextStep = steps[nextStepIndex];

      if (nextStep) {
        setStepIndex(nextStepIndex);
        if (location.pathname !== nextStep.route) {
          setRun(false);
          navigate(nextStep.route);
          setTimeout(() => setRun(true), 400);
        }
      } else if (action === ACTIONS.NEXT || action === ACTIONS.CLOSE) {
        finish();
      }
    } else if (action === ACTIONS.SKIP) {
      dismiss();
    }
  };

  if (!mounted) return null;
  // Don't render for waiter role
  if (user?.role === 'waiter') return null;

  return (
    <Joyride
      onEvent={handleJoyrideCallback}
      continuous
      run={run}
      stepIndex={stepIndex}
      steps={steps}
      scrollToFirstStep
      options={{
        zIndex: 10000,
        primaryColor: appTheme.colors.primary.main,
        backgroundColor: '#ffffff',
        textColor: appTheme.colors.text.primary,
        arrowColor: '#ffffff',
        skipBeacon: true,
        overlayClickAction: false,
      }}
      styles={{
        tooltipContainer: {
          textAlign: isRTL ? 'right' : 'left',
          fontFamily,
          direction: isRTL ? 'rtl' : 'ltr',
        },
        buttonPrimary: {
          backgroundColor: appTheme.colors.primary.main,
          fontFamily,
          borderRadius: 8,
          padding: '8px 16px',
        },
        buttonBack: {
          color: appTheme.colors.text.secondary,
          fontFamily,
          marginRight: isRTL ? 0 : 10,
          marginLeft: isRTL ? 10 : 0,
        },
        buttonSkip: {
          color: appTheme.colors.text.secondary,
          fontFamily,
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        }
      }}
      locale={{
        back: t('onboarding.back', { defaultValue: 'السابق' }),
        close: t('common.close', { defaultValue: 'إغلاق' }),
        last: t('onboarding.finish', { defaultValue: 'إنهاء الجولة' }),
        next: t('onboarding.next', { defaultValue: 'التالي' }),
        skip: t('onboarding.skip', { defaultValue: 'تخطي' }),
      }}
    />
  );
};
